import {
  ArrowPathIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import {
  ReactNode, useCallback, useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Link, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import stationImage from '../../assets/images/station.svg';
import Spinner from '../../components/spinner';
import {
  clearAuthReturnTo,
  locationPath,
  saveAuthReturnTo,
} from '../../helpers/auth-return';
import createUuid from '../../helpers/uuid';
import { RootState } from '../../redux/store';
import Api from '../../services';
import useDispatchStatus from './hooks/use-dispatch-status';
import useStationContext from './hooks/use-station-context';

const attemptKey = (stationCode: string) => `station_dispatch_attempt:${stationCode}`;

const getApiErrorCode = (error: unknown) => {
  const { response } = error as {
    response?: { data?: { errors?: Array<{ message?: string; type?: string }> } };
  };
  const apiError = response?.data?.errors?.[0];
  return apiError?.message || apiError?.type || 'dispatch_failed';
};

type ErrorContent = {
  title: string;
  subtitle: string;
};

function JourneyCard({ children }: { children: ReactNode }) {
  return (
    <section className='relative w-full rounded-2xl bg-white p-6 text-center shadow-lg sm:p-8'>
      {children}
    </section>
  );
}

function ErrorState({
  errorCode,
  stationCode,
  onRetry,
}: {
  errorCode: string;
  stationCode: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useParams();
  const content = t(`stationJourney.errors.${errorCode}`, {
    returnObjects: true,
    defaultValue: t('stationJourney.errors.dispatch_failed', { returnObjects: true }),
  }) as ErrorContent;
  const showAppFallback = [
    'dispatch_failed',
    'failed',
    'no_active_subscription',
    'no_batteries',
    'no_powerbanks_in_grid',
    'offline',
    'pending_order',
    'status_unavailable',
    'timed_out',
  ].includes(errorCode);

  return (
    <JourneyCard>
      {onRetry && (
        <button
          type='button'
          onClick={onRetry}
          aria-label={t('stationJourney.retry')}
          title={t('stationJourney.retry')}
          className='absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-secondary transition hover:border-primary hover:bg-lime-50 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
        >
          <ArrowPathIcon className='h-5 w-5' aria-hidden='true' />
        </button>
      )}
      <ExclamationTriangleIcon className='mx-auto h-16 w-16 text-amber-500' aria-hidden='true' />
      <h1 className='mt-5 text-2xl font-semibold text-black'>{content.title}</h1>
      <p className='mt-3 text-base leading-7 text-secondary'>{content.subtitle}</p>
      <p className='mt-4 text-sm text-gray-500'>
        {t('stationJourney.stationCode', { stationCode })}
      </p>
      {showAppFallback && (
        <Link
          to={`/${locale}/download`}
          className='mt-6 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-3 text-base font-medium text-white hover:bg-lime-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
        >
          {t('stationJourney.openApp')}
        </Link>
      )}
    </JourneyCard>
  );
}

function OrderStatus() {
  const {
    stationCode, orderId,
  } = useParams();
  const { t } = useTranslation();
  const {
    order, loading, errorCode, refresh,
  } = useDispatchStatus(orderId);

  useEffect(() => {
    if (order?.dispatch_status === 'successful' && stationCode) {
      sessionStorage.removeItem(attemptKey(stationCode));
      clearAuthReturnTo();
    }
  }, [order?.dispatch_status, stationCode]);

  if (loading && !order) {
    return (
      <JourneyCard>
        <Spinner />
        <h1 className='mt-5 text-2xl font-semibold text-black'>{t('stationJourney.dispatching.title')}</h1>
        <p className='mt-3 text-secondary'>{t('stationJourney.dispatching.subtitle')}</p>
      </JourneyCard>
    );
  }

  if (errorCode && !order) {
    return <ErrorState errorCode={errorCode} stationCode={stationCode || ''} onRetry={refresh} />;
  }

  if (order?.dispatch_status === 'successful') {
    return (
      <JourneyCard>
        <CheckCircleIcon className='mx-auto h-20 w-20 text-primary' aria-hidden='true' />
        <h1 className='mt-5 text-2xl font-semibold text-black'>{t('stationJourney.success.title')}</h1>
        <p className='mt-3 text-base leading-7 text-secondary'>{t('stationJourney.success.subtitle')}</p>
      </JourneyCard>
    );
  }

  if (order?.dispatch_status === 'failed' || order?.dispatch_status === 'timed_out') {
    return (
      <ErrorState
        errorCode={order.dispatch_error_code || order.dispatch_status}
        stationCode={stationCode || ''}
        onRetry={refresh}
      />
    );
  }

  return (
    <JourneyCard>
      <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-lime-50'>
        <BoltIcon className='h-10 w-10 animate-pulse text-primary' aria-hidden='true' />
      </div>
      <h1 className='mt-5 text-2xl font-semibold text-black'>{t('stationJourney.dispatching.title')}</h1>
      <p className='mt-3 text-base leading-7 text-secondary'>{t('stationJourney.dispatching.subtitle')}</p>
      <p className='mt-5 text-sm text-gray-500'>{t('stationJourney.dispatching.keepOpen')}</p>
    </JourneyCard>
  );
}

export default function StationJourney() {
  const {
    locale, stationCode, orderId,
  } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const context = useStationContext(stationCode);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  const startDispatch = useCallback(async () => {
    if (!stationCode || dispatching) return;

    setDispatching(true);
    setDispatchError(null);

    let idempotencyKey = sessionStorage.getItem(attemptKey(stationCode));
    if (!idempotencyKey) {
      idempotencyKey = createUuid();
      sessionStorage.setItem(attemptKey(stationCode), idempotencyKey);
    }

    try {
      const response = await Api.orders.createStationDispatch(stationCode, idempotencyKey);
      navigate(`/${locale}/station/${encodeURIComponent(stationCode)}/order/${response.result.id}`, { replace: true });
    } catch (error: unknown) {
      sessionStorage.removeItem(attemptKey(stationCode));
      setDispatchError(getApiErrorCode(error));
      setDispatching(false);
    }
  }, [dispatching, locale, navigate, stationCode]);

  if (orderId) return <OrderStatus />;

  if (context.loading) {
    return (
      <JourneyCard>
        <Spinner />
        <p className='mt-4 text-secondary'>{t('stationJourney.loading')}</p>
      </JourneyCard>
    );
  }

  const errorCode = dispatchError || context.errorCode;
  if (errorCode) {
    return (
      <ErrorState
        errorCode={errorCode}
        stationCode={stationCode || ''}
        onRetry={dispatchError ? startDispatch : context.refresh}
      />
    );
  }

  const continueToDispatch = () => {
    if (!stationCode) return;

    if (authenticated) {
      startDispatch();
      return;
    }

    saveAuthReturnTo(locationPath(location));
    navigate(`/${locale}/auth/login`, { state: { from: location } });
  };

  return (
    <JourneyCard>
      <img src={stationImage} alt='' className='mx-auto h-32 w-32 object-contain' />
      <h1 className='mt-5 text-2xl font-semibold text-black'>
        {context.data?.name || t('stationJourney.stationTitle')}
      </h1>
      {context.data?.address && (
        <p className='mt-3 inline-flex items-center justify-center gap-2 text-sm text-secondary'>
          <MapPinIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
          {context.data.address}
        </p>
      )}
      <p className='mt-3 text-sm text-gray-500'>
        {t('stationJourney.stationCode', { stationCode })}
      </p>
      <p className='mt-6 text-base leading-7 text-secondary'>{t('stationJourney.readySubtitle')}</p>
      <button
        type='button'
        onClick={continueToDispatch}
        disabled={dispatching}
        className='mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-base font-medium text-white hover:bg-lime-600 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
      >
        {dispatching ? <Spinner /> : <BoltIcon className='h-5 w-5' aria-hidden='true' />}
        {authenticated
          ? t('stationJourney.releaseButton')
          : t('stationJourney.loginButton')}
      </button>
    </JourneyCard>
  );
}
